import type { CSSProperties } from 'react';
import type { LocalPlayerProfile } from './playerProfile';
import type { MatchAwardSnapshot } from '../shared/protocol';

type PostMatchPlayer = {
  id: string;
  displayName: string;
  team: string | null;
  score: number;
  kills?: number;
  deaths?: number;
  captures?: number;
  returns?: number;
};

type PostMatchSummaryProps = {
  visible: boolean;
  winnerTeam: string | null;
  teamScores: Record<string, number>;
  players: PostMatchPlayer[];
  countdownSeconds: number;
  localPlayerId?: string;
  objectiveTelemetry?: Array<{
    team: string | null;
    captures?: number;
    returns?: number;
    carrierKills?: number;
    standoffs?: number;
    generatorDestroys?: number;
    generatorRepairs?: number;
  }>;
  awards?: MatchAwardSnapshot[];
  careerProfile?: LocalPlayerProfile | null;
};

const overlayStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(4, 8, 14, 0.88)',
  color: '#edf7ff',
  zIndex: 110,
};

function teamTone(team: string | null) {
  if (team === 'red') return '#ff7d7d';
  if (team === 'blue') return '#70d6ff';
  return '#9db6c8';
}

function resultLabel(winnerTeam: string | null): string {
  if (winnerTeam === 'red') return 'RED VICTORY';
  if (winnerTeam === 'blue') return 'BLUE VICTORY';
  return 'DRAW';
}

function whyWon(winnerTeam: string | null, players: PostMatchPlayer[], objectiveTelemetry: PostMatchSummaryProps['objectiveTelemetry'] = []) {
  if (!winnerTeam) {
    return 'Even scoreline. Review route control and returns.';
  }

  const teamTelemetry = objectiveTelemetry.find((entry) => entry.team === winnerTeam);
  const teamPlayers = players.filter((player) => player.team === winnerTeam);
  const captures = teamPlayers.reduce((total, player) => total + (player.captures ?? 0), 0);
  const returns = teamPlayers.reduce((total, player) => total + (player.returns ?? 0), 0);
  const kills = teamPlayers.reduce((total, player) => total + (player.kills ?? 0), 0);

  if ((teamTelemetry?.generatorDestroys ?? 0) > 0) {
    return `${winnerTeam.toUpperCase()} won by breaking base power and converting generator pressure.`;
  }
  if ((teamTelemetry?.generatorRepairs ?? 0) > 0) {
    return `${winnerTeam.toUpperCase()} won by restoring base power and holding the route window.`;
  }
  if ((teamTelemetry?.standoffs ?? 0) > 0) {
    return `${winnerTeam.toUpperCase()} won by managing the standoff and forcing the return.`;
  }
  if (captures > 0) {
    return `${winnerTeam.toUpperCase()} won through ${captures} capture${captures === 1 ? '' : 's'} and route pressure.`;
  }
  if (returns >= kills / 2) {
    return `${winnerTeam.toUpperCase()} won by denying carriers and converting returns.`;
  }
  return `${winnerTeam.toUpperCase()} won by suppressing midfield and cleanup fights.`;
}

export function PostMatchSummary({ visible, winnerTeam, teamScores, players, countdownSeconds, localPlayerId, objectiveTelemetry, awards = [], careerProfile = null }: PostMatchSummaryProps) {
  if (!visible) return null;

  const accent = teamTone(winnerTeam);
  const sorted = [...players].sort((a, b) => b.score - a.score);
  const mvp = sorted[0];
  const localPlayer = localPlayerId ? players.find((p) => p.id === localPlayerId) : null;

  return (
    <div style={overlayStyle}>
      <div
        style={{
          width: 'min(520px, 88vw)',
          padding: 24,
          background: 'linear-gradient(180deg, rgba(7, 12, 20, 0.98), rgba(4, 8, 14, 0.96))',
          border: `1px solid ${accent}35`,
          boxShadow: `0 0 60px ${accent}15`,
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 10, letterSpacing: 2.6, textTransform: 'uppercase', color: 'rgba(231,244,252,0.5)', marginBottom: 8 }}>
          Match Complete
        </div>
        <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', color: accent, marginBottom: 16 }}>
          {resultLabel(winnerTeam)}
        </div>
        <div style={{ margin: '-6px auto 16px', maxWidth: 390, fontSize: 11, lineHeight: 1.4, color: 'rgba(231,244,252,0.66)', textTransform: 'uppercase', letterSpacing: 0.8 }}>
          {whyWon(winnerTeam, players, objectiveTelemetry)}
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 32, marginBottom: 20 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 9.5, letterSpacing: 1.6, textTransform: 'uppercase', color: teamTone('red'), marginBottom: 4 }}>Red</div>
            <div style={{ fontSize: 36, fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{teamScores.red ?? 0}</div>
          </div>
          <div style={{ fontSize: 24, color: 'rgba(231,244,252,0.3)', alignSelf: 'center' }}>&mdash;</div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 9.5, letterSpacing: 1.6, textTransform: 'uppercase', color: teamTone('blue'), marginBottom: 4 }}>Blue</div>
            <div style={{ fontSize: 36, fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{teamScores.blue ?? 0}</div>
          </div>
        </div>

        {mvp && (
          <div style={{ marginBottom: 16, padding: '8px 12px', background: `${accent}10`, border: `1px solid ${accent}25` }}>
            <div style={{ fontSize: 9, letterSpacing: 1.4, textTransform: 'uppercase', color: accent, marginBottom: 4 }}>MVP</div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>{mvp.displayName}</div>
            <div style={{ fontSize: 10, color: 'rgba(231,244,252,0.6)', marginTop: 2 }}>
              {mvp.score} pts &middot; {mvp.kills ?? 0}K &middot; {mvp.captures ?? 0} cap
            </div>
          </div>
        )}

        {localPlayer && (
          <div style={{ marginBottom: 16, fontSize: 11, color: 'rgba(231,244,252,0.6)' }}>
            Your stats: {localPlayer.score} pts &middot; {localPlayer.kills ?? 0}K / {localPlayer.deaths ?? 0}D &middot; {localPlayer.captures ?? 0} cap &middot; {localPlayer.returns ?? 0} ret
          </div>
        )}

        {awards.length > 0 && (
          <div style={{ marginBottom: 16, padding: '10px 12px', background: 'rgba(255,204,102,0.07)', border: '1px solid rgba(255,204,102,0.2)', textAlign: 'left' }}>
            <div style={{ fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: '#ffcc66', marginBottom: 8, textAlign: 'center' }}>Match Awards</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(132px, 1fr))', gap: 8 }}>
              {awards.slice(0, 6).map((award) => (
                <div key={`${award.kind}-${award.playerId}`} style={{ padding: '6px 8px', background: 'rgba(231,244,252,0.045)', border: '1px solid rgba(231,244,252,0.08)' }}>
                  <div style={{ fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: award.playerId === localPlayerId ? accent : '#ffcc66' }}>{award.label}</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#edf7ff', marginTop: 2 }}>{award.playerName}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {careerProfile && (
          <div style={{ marginBottom: 16, fontSize: 10.5, letterSpacing: 0.9, textTransform: 'uppercase', color: 'rgba(231,244,252,0.62)' }}>
            <span style={{ color: accent, fontWeight: 700 }}>Career Snapshot</span> &middot; {careerProfile.matchesPlayed} matches &middot; {careerProfile.wins} wins &middot; {careerProfile.kills}K/{careerProfile.deaths}D &middot; {careerProfile.captures} cap &middot; {careerProfile.returns} ret{careerProfile.favoriteMap ? ` · fav ${careerProfile.favoriteMap}` : ''}
          </div>
        )}

        <div style={{ fontSize: 13, letterSpacing: 1.8, textTransform: 'uppercase', color: '#ffcc66', marginTop: 12 }}>
          NEW ROUND STARTING... {Math.max(0, Math.ceil(countdownSeconds))}S
        </div>
      </div>
    </div>
  );
}
